import React, { useState, useRef } from 'react';
import { X, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { useAuth } from '../../contexts/AuthContext';

interface ImportLeadsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (leads: any[]) => Promise<{ ok: boolean; error?: string; successCount?: number; errorCount?: number }>;
}

interface ParsedLead {
  first_name: string;
  last_name: string;
  emails: string[];
  phone?: string;
  source?: string;
  priority?: string;
  status?: string;
  interested_countries?: string[];
  interested_programs?: string[];
  notes?: string;
  budget?: string;
  english_level?: string;
  education_level?: string;
  nationality?: string;
  interested_service?: string;
  contact_role?: 'student' | 'guardian';
  guardian_info?: {
    name: string;
    email: string;
    phone: string;
    relationship: string;
  };
  student_info?: {
    first_name: string;
    last_name: string;
    name: string;
    email: string;
    emails: string[];
    phone: string;
    education_level: string;
    english_level: string;
    nationality: string;
  };
}

const ImportLeadsModal: React.FC<ImportLeadsModalProps> = ({ isOpen, onClose, onImport }) => {
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [leads, setLeads] = useState<ParsedLead[]>([]);
  const [importing, setImporting] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{ success: number; failed: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setParseError(null);
    setImportResult(null);

    try {
      const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();

      if (fileExtension === 'csv') {
        Papa.parse(selectedFile, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const parsedLeads = parseLeadsData(results.data);
            setLeads(parsedLeads);
          },
          error: (error) => {
            setParseError(`CSV parse hatası: ${error.message}`);
          },
        });
      } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = e.target?.result;
            const workbook = XLSX.read(data, { type: 'binary' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet);
            const parsedLeads = parseLeadsData(jsonData);
            setLeads(parsedLeads);
          } catch (error: any) {
            setParseError(`Excel parse hatası: ${error.message}`);
          }
        };
        reader.readAsBinaryString(selectedFile);
      } else {
        setParseError('Desteklenmeyen dosya formatı. Lütfen CSV veya Excel dosyası yükleyin.');
      }
    } catch (error: any) {
      setParseError(`Dosya okuma hatası: ${error.message}`);
    }
  };

  const parseLeadsData = (data: any[]): ParsedLead[] => {
    return data
      .map((row: any) => {
        const studentFullName = row['ÖĞRENCİ İSİM SOYİSİM'] || row['Ad Soyad'] || '';
        const guardianFullName = row['VELİ İSİM SOYİSİM'] || row['AD SOYAD'] || '';

        let firstName = row['first_name'] || row['First Name'] || row['Ad'] || row['İsim'] || '';
        let lastName = row['last_name'] || row['Last Name'] || row['Soyad'] || '';

        if (studentFullName && !firstName && !lastName) {
          const nameParts = studentFullName.trim().split(/\s+/);
          if (nameParts.length >= 2) {
            firstName = nameParts[0];
            lastName = nameParts.slice(1).join(' ');
          } else {
            firstName = nameParts[0];
            lastName = '';
          }
        }

        const studentEmail = row['ÖĞRENCİ MAİL'] || row['E-Posta Adresi'] || '';
        const guardianEmail = row['VELİ MAİL'] || row['E-Posta Adresi'] || '';

        let emailStr = row['email'] || row['Email'] || row['E-posta'] || row['emails'] || row['Emails'] || '';

        if (!emailStr) {
          if (studentEmail) {
            emailStr = studentEmail;
          } else if (guardianEmail) {
            emailStr = guardianEmail;
          }
        }

        if (!firstName || !emailStr) {
          return null;
        }

        const emailsArray = emailStr.includes(',')
          ? emailStr.split(',').map((e: string) => e.trim().toLowerCase()).filter((e: string) => e)
          : [emailStr.trim().toLowerCase()];

        const studentPhone = row['ÖĞRENCİ TEL'] || row['Telefon Numarası'] || '';
        const guardianPhone = row['VELİ TEL'] || row['Telefon Numarası'] || '';

        let phoneStr = (row['phone'] || row['Phone'] || row['Telefon'] || '').toString().trim();
        if (!phoneStr) {
          phoneStr = studentPhone ? studentPhone.toString().trim() : (guardianPhone ? guardianPhone.toString().trim() : '');
        }

        const interestedProgramStr = row['EĞİTİM ALMAK İSTEDİĞİ BÖLÜM'] || row['Eğitim Almak İstediği Bölüm'] || '';
        const interestedCountriesStr = row['interested_countries'] || row['Interested Countries'] || row['İlgilendiği Ülkeler'] || '';
        const interestedProgramsStr = row['interested_programs'] || row['Interested Programs'] || row['İlgilendiği Programlar'] || interestedProgramStr || '';

        const schoolStr = row['OKULU'] || row['Okul'] || '';
        const englishLevelStr = row['İNGİLİZCE SEVİYESİ'] || row['İngilizce Seviyesi'] || row['english_level'] || row['English Level'] || '';
        const notesStr = row['NOT'] || row['Not'] || row['notes'] || row['Notes'] || row['Notlar'] || '';
        const statusStr = row['Durumu'] || row['Status'] || row['status'] || row['Durum'] || 'new_lead';

        const hasGuardianInfo = guardianFullName || guardianEmail || guardianPhone;

        let guardianRelationship = 'diger';
        if (guardianFullName) {
          const nameLower = guardianFullName.toLowerCase();
          if (nameLower.includes('anne') || nameLower.includes('ana')) {
            guardianRelationship = 'anne';
          } else if (nameLower.includes('baba') || nameLower.includes('father')) {
            guardianRelationship = 'baba';
          }
        }

        const studentNameParts = studentFullName ? studentFullName.trim().split(/\s+/) : [firstName, lastName];
        const studentFirstName = studentNameParts[0] || firstName;
        const studentLastName = studentNameParts.length > 1 ? studentNameParts.slice(1).join(' ') : lastName;

        const lead: ParsedLead = {
          first_name: firstName.trim(),
          last_name: lastName || 'Bilinmiyor',
          emails: emailsArray,
          phone: phoneStr,
          source: row['source'] || row['Source'] || row['Kaynak'] || 'import',
          priority: row['priority'] || row['Priority'] || row['Öncelik'] || 'medium',
          status: statusStr,
          interested_countries: interestedCountriesStr ? interestedCountriesStr.split(',').map((c: string) => c.trim()) : ['İtalya'],
          interested_programs: interestedProgramsStr ? interestedProgramsStr.split(',').map((p: string) => p.trim()) : [],
          notes: notesStr,
          budget: row['budget'] || row['Budget'] || row['Bütçe'] || '',
          english_level: englishLevelStr,
          education_level: schoolStr || row['education_level'] || row['Education Level'] || row['Eğitim Seviyesi'] || '',
          nationality: row['nationality'] || row['Nationality'] || row['Uyruk'] || 'Türkiye',
          interested_service: row['interested_service'] || row['Interested Service'] || row['İlgilendiği Hizmet'] || 'education',
          contact_role: hasGuardianInfo ? 'guardian' : 'student',
          guardian_info: hasGuardianInfo ? {
            name: guardianFullName || '',
            email: guardianEmail || '',
            phone: guardianPhone || '',
            relationship: guardianRelationship
          } : undefined,
          student_info: hasGuardianInfo ? {
            first_name: studentFirstName,
            last_name: studentLastName || 'Bilinmiyor',
            name: studentFullName || `${studentFirstName} ${studentLastName}`,
            email: studentEmail || '',
            emails: studentEmail ? [studentEmail] : [],
            phone: studentPhone || '',
            education_level: schoolStr || '',
            english_level: englishLevelStr || '',
            nationality: row['nationality'] || row['Nationality'] || row['Uyruk'] || 'Türkiye'
          } : undefined,
        };

        return lead;
      })
      .filter((lead): lead is ParsedLead => lead !== null);
  };

  const handleImport = async () => {
    if (leads.length === 0) {
      setParseError('İçe aktarılacak lead bulunamadı.');
      return;
    }

    setImporting(true);
    setParseError(null);

    try {
      const leadsWithAssignment = leads.map((lead) => ({
        ...lead,
        assigned_to: user?.id,
        created_by: user?.id,
        branch_id: user?.branchId,
      }));

      const result = await onImport(leadsWithAssignment);

      if (result.ok) {
        setImportResult({
          success: result.successCount || leads.length,
          failed: result.errorCount || 0,
        });
        setLeads([]);
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setParseError(result.error || 'İçe aktarma sırasında hata oluştu.');
      }
    } catch (error: any) {
      setParseError(`İçe aktarma hatası: ${error.message}`);
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = [
      {
        'ÖĞRENCİ İSİM SOYİSİM': 'Ahmet Yılmaz',
        'OKULU': 'Atatürk Lisesi',
        'EĞİTİM ALMAK İSTEDİĞİ BÖLÜM': 'Tıp,Mimarlık',
        'İNGİLİZCE SEVİYESİ': 'B2',
        'ÖĞRENCİ TEL': '+905551234567',
        'ÖĞRENCİ MAİL': 'ahmet@example.com',
        'VELİ İSİM SOYİSİM': 'Mehmet Yılmaz (Baba)',
        'VELİ TEL': '+905559999999',
        'VELİ MAİL': 'mehmet@example.com',
        'NOT': 'İlk görüşme yapıldı',
        'Durumu': 'new_lead',
      },
      {
        'ÖĞRENCİ İSİM SOYİSİM': 'Ayşe Demir',
        'OKULU': 'İstanbul Lisesi',
        'EĞİTİM ALMAK İSTEDİĞİ BÖLÜM': 'İşletme',
        'İNGİLİZCE SEVİYESİ': 'C1',
        'ÖĞRENCİ TEL': '+905559876543',
        'ÖĞRENCİ MAİL': 'ayse@example.com',
        'VELİ İSİM SOYİSİM': '',
        'VELİ TEL': '',
        'VELİ MAİL': '',
        'NOT': 'Dökümanlar bekleniyor',
        'Durumu': 'contacted',
      },
      {
        'ÖĞRENCİ İSİM SOYİSİM': 'Zeynep Kaya',
        'OKULU': 'Ankara Fen Lisesi',
        'EĞİTİM ALMAK İSTEDİĞİ BÖLÜM': 'Mühendislik',
        'İNGİLİZCE SEVİYESİ': 'B1',
        'ÖĞRENCİ TEL': '+905557777777',
        'ÖĞRENCİ MAİL': 'zeynep@example.com',
        'VELİ İSİM SOYİSİM': 'Fatma Kaya (Anne)',
        'VELİ TEL': '+905558888888',
        'VELİ MAİL': 'fatma@example.com',
        'NOT': 'Detaylı bilgi verildi',
        'Durumu': 'new_lead',
      },
    ];

    const csv = Papa.unparse(template);
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'lead_import_template.csv';
    link.click();
  };

  const handleClose = () => {
    if (!importing) {
      setFile(null);
      setLeads([]);
      setParseError(null);
      setImportResult(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Lead İçe Aktar</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">CSV veya Excel dosyasından lead ekle</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={importing}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">Dosya Formatı</h3>
                <p className="text-sm text-blue-700 dark:text-blue-400 mb-2">
                  CSV veya Excel dosyanız aşağıdaki formatta olmalıdır. Aşağıdaki tablo örneğini kullanarak dosyanızı hazırlayabilirsiniz.
                </p>
                <ul className="text-sm text-blue-700 dark:text-blue-400 list-disc list-inside space-y-1 mb-3">
                  <li><strong>Zorunlu Sütunlar:</strong> ÖĞRENCİ İSİM SOYİSİM, ÖĞRENCİ MAİL (veya VELİ MAİL)</li>
                  <li><strong>Opsiyonel Sütunlar:</strong> OKULU, EĞİTİM ALMAK İSTEDİĞİ BÖLÜM, İNGİLİZCE SEVİYESİ, telefon ve mail bilgileri, NOT, Durumu</li>
                  <li><strong>Veli Bilgileri:</strong> Veli bilgileri varsa lead "Veli" olarak, yoksa "Öğrenci" olarak kaydedilir</li>
                  <li><strong>Bölüm/Program:</strong> Virgülle ayırarak birden fazla bölüm yazabilirsiniz (örn: Tıp,Mimarlık)</li>
                </ul>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-300">Örnek Tablo:</p>
                  <button
                    onClick={downloadTemplate}
                    className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors text-xs"
                  >
                    <Download className="w-3 h-3" />
                    <span>CSV Şablon İndir</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold whitespace-nowrap">ÖĞRENCİ İSİM SOYİSİM</th>
                    <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold whitespace-nowrap">OKULU</th>
                    <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold whitespace-nowrap">EĞİTİM ALMAK İSTEDİĞİ BÖLÜM</th>
                    <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold whitespace-nowrap">İNGİLİZCE SEVİYESİ</th>
                    <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold whitespace-nowrap">ÖĞRENCİ TEL</th>
                    <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold whitespace-nowrap">ÖĞRENCİ MAİL</th>
                    <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold whitespace-nowrap">VELİ İSİM SOYİSİM</th>
                    <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold whitespace-nowrap">VELİ TEL</th>
                    <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold whitespace-nowrap">VELİ MAİL</th>
                    <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold whitespace-nowrap">NOT</th>
                    <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold whitespace-nowrap">Durumu</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="py-2 px-3 text-gray-900 dark:text-white">Ahmet Yılmaz</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">Atatürk Lisesi</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">Tıp,Mimarlık</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">B2</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">+905551234567</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">ahmet@example.com</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">Mehmet Yılmaz (Baba)</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">+905559999999</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">mehmet@example.com</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">İlk görüşme yapıldı</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">new_lead</td>
                  </tr>
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="py-2 px-3 text-gray-900 dark:text-white">Ayşe Demir</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">İstanbul Lisesi</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">İşletme</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">C1</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">+905559876543</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">ayse@example.com</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400"></td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400"></td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400"></td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">Dökümanlar bekleniyor</td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400">contacted</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-4 py-2">
              <p className="text-xs text-gray-600 dark:text-gray-400">
                <strong>Bu tablodaki sütun isimlerini aynen kullanın.</strong> Veli bilgileri opsiyoneldir. Eğer veli bilgisi varsa lead "Veli" olarak, yoksa "Öğrenci" olarak kaydedilir.
              </p>
            </div>
          </div>

          <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-8">
            <div className="text-center">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <div className="mb-4">
                <label htmlFor="file-upload" className="cursor-pointer">
                  <span className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
                    Dosya seç
                  </span>
                  <span className="text-gray-600 dark:text-gray-400"> veya sürükle bırak</span>
                </label>
                <input
                  ref={fileInputRef}
                  id="file-upload"
                  type="file"
                  className="hidden"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  disabled={importing}
                />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">CSV, XLSX veya XLS (Max 10MB)</p>
            </div>
          </div>

          {file && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FileSpreadsheet className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{file.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {leads.length} lead bulundu
                    </p>
                  </div>
                </div>
                {!importing && (
                  <button
                    onClick={() => {
                      setFile(null);
                      setLeads([]);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {parseError && (
            <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-900 dark:text-red-300 mb-1">Hata</h3>
                  <p className="text-sm text-red-700 dark:text-red-400">{parseError}</p>
                </div>
              </div>
            </div>
          )}

          {importResult && (
            <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-green-900 dark:text-green-300 mb-1">İçe Aktarma Tamamlandı</h3>
                  <p className="text-sm text-green-700 dark:text-green-400">
                    {importResult.success} lead başarıyla eklendi
                    {importResult.failed > 0 && `, ${importResult.failed} lead eklenemedi`}
                  </p>
                </div>
              </div>
            </div>
          )}

          {leads.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Önizleme (İlk 5 kayıt)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-medium">Ad</th>
                      <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-medium">Soyad</th>
                      <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-medium">Email</th>
                      <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-medium">Telefon</th>
                      <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-medium">Kaynak</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {leads.slice(0, 5).map((lead, index) => (
                      <tr key={index}>
                        <td className="py-2 px-3 text-gray-900 dark:text-white">{lead.first_name}</td>
                        <td className="py-2 px-3 text-gray-900 dark:text-white">{lead.last_name}</td>
                        <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{lead.emails[0]}</td>
                        <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{lead.phone || '-'}</td>
                        <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{lead.source || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {leads.length > 5 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                  ... ve {leads.length - 5} lead daha
                </p>
              )}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 px-6 py-4 flex items-center justify-end space-x-3">
          <button
            onClick={handleClose}
            disabled={importing}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
          >
            İptal
          </button>
          <button
            onClick={handleImport}
            disabled={leads.length === 0 || importing}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {importing ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                <span>İçe Aktarılıyor...</span>
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                <span>İçe Aktar ({leads.length} lead)</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportLeadsModal;
